import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { CheckCircle2, AlertCircle, TrendingUp, Lightbulb, Star, Sparkles } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const FeedbackShowcase = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [animatedScores, setAnimatedScores] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);
  const [showCoachingTip, setShowCoachingTip] = useState(false);
  const isMobile = useIsMobile();

  // Memoize particles to avoid recreating on every render - reduce count on mobile
  const particles = useMemo(() => {
    const count = isMobile ? 5 : 15;
    return [...Array(count)].map((_, i) => ({
      id: i,
      left: `${10 + Math.random() * 80}%`,
      top: `${Math.random() * 100}%`,
      duration: `${4 + Math.random() * 3}s`,
      delay: `${Math.random() * 5}s`,
    }));
  }, [isMobile]);

  const rubricScores = [
    { category: 'Opening', score: 85, color: 'success' },
    { category: 'Needs Identification', score: 78, color: 'primary' },
    { category: 'Solution Presentation', score: 72, color: 'warning' },
    { category: 'Objection Handling', score: 88, color: 'success' },
    { category: 'Close', score: 65, color: 'error' },
    { category: 'Communication', score: 82, color: 'primary' },
    { category: 'Overall', score: 78, color: 'primary' },
  ];

  // Animate scores when visible
  useEffect(() => {
    if (!isVisible) return;
    
    const duration = 1200;
    const steps = 30;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedScores(rubricScores.map(item => Math.round(item.score * eased)));
      
      if (step >= steps) {
        clearInterval(timer);
        setTimeout(() => setShowCoachingTip(true), 300);
      }
    }, interval);
    
    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 relative overflow-hidden">
      {/* Background decoration removed per Rule 3/5 */}
      
      {/* Animated particles - monochrome per Rule 8 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute w-1 h-1 bg-muted-foreground/10 rounded-full will-change-transform"
            style={{
              left: particle.left,
              top: particle.top,
              animation: `float ${particle.duration} ease-in-out infinite`,
              animationDelay: particle.delay,
            }}
          />
        ))}
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Feedback mockup */}
          <div className={`relative order-2 lg:order-1 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            {/* Glow removed per Rule 6 */}
            
            {/* Feedback card mockup */}
            <div className="relative bg-card rounded-xl overflow-hidden border border-border shadow-elevation-1 transition-all duration-300">
              {/* Header */}
              <div className="px-5 py-4 bg-muted/30 border-b border-border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <h3 className="font-sans font-semibold text-foreground">Session Feedback</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1,2,3,4].map(i => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 transition-all duration-300 ${isVisible ? 'text-primary fill-primary' : 'text-muted-foreground'}`}
                          style={{ transitionDelay: `${800 + i * 100}ms` }}
                        />
                      ))}
                      <Star 
                        className={`w-4 h-4 transition-all duration-300 ${isVisible ? 'text-primary fill-primary/50' : 'text-muted-foreground'}`}
                        style={{ transitionDelay: `1200ms` }}
                      />
                    </div>
                    <span className="font-sans tabular-nums text-2xl font-bold text-foreground">{animatedScores[6]}</span>
                    <span className="text-muted-foreground">/100</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Session Analysis</p>
              </div>

              {/* Scores with animated bars */}
              <div className="p-5 space-y-3 bg-card">
                {rubricScores.map((item, index) => (
                  <div 
                    key={item.category}
                    className={`group transition-all duration-500 hover:bg-muted/50 rounded-xl p-2 -mx-2 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                    style={{ transitionDelay: `${200 + index * 80}ms` }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{item.category}</span>
                      <span className={`text-sm font-semibold tabular-nums transition-all duration-300 ${
                        animatedScores[index] >= 80 ? 'text-status-success' : 
                        animatedScores[index] >= 70 ? 'text-primary' : 
                        animatedScores[index] >= 60 ? 'text-status-warning' : 'text-status-error'
                      }`}>
                        {animatedScores[index]}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden ${
                          item.score >= 80 ? 'bg-status-success-fg' : 
                          item.score >= 70 ? 'bg-primary' : 
                          item.score >= 60 ? 'bg-status-warning-fg' : 'bg-status-error-fg'
                        }`}
                        style={{ 
                          width: isVisible ? `${item.score}%` : '0%',
                          transitionDelay: `${400 + index * 100}ms`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Coaching tip with entrance animation */}
              <div className={`px-5 py-4 bg-muted/50 border-t border-border transition-all duration-500 ${showCoachingTip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-1">Judge's Coaching Tip</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Strong discovery questions! Focus on transitioning to your close more naturally. 
                      Try summarizing the buyer's needs before asking for commitment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Text content */}
          <div className={`order-1 lg:order-2 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <span className="inline-flex items-center gap-2 mb-4 rounded-full border border-subtle bg-card px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <CheckCircle2 className="w-4 h-4" />
              Objective Intelligence
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight mb-5">
              Know Exactly{' '}
              <span className="relative inline-block">
                <span className="text-primary">
                  Where to Improve
                </span>
              </span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              Get scored on multiple rubric categories with specific, actionable feedback.
            </p>

            <div className="space-y-4">
              {[
                { icon: CheckCircle2, title: 'Rubric-Aligned Scoring', desc: 'Every category matches the official competition rubric exactly', color: 'success' },
                { icon: TrendingUp, title: 'SPIN Breakdown', desc: 'See detailed analysis of your Situation, Problem, Implication, and Need-Payoff questions', color: 'primary' },
                { icon: AlertCircle, title: 'Specific Improvements', desc: 'Not generic advice. Feedback references exactly what you said and how to improve it', color: 'warning' },
              ].map((feature, index) => (
                <div 
                  key={feature.title}
                  className={`flex items-start gap-4 p-3 rounded-xl transition-all duration-500 hover:bg-muted group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${
                    feature.color === 'success' ? 'bg-status-success-bg text-status-success' :
                    feature.color === 'warning' ? 'bg-status-warning-bg text-status-warning' :
                    'bg-primary/10 text-primary'
                  }`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1" role="heading" aria-level={4}>{feature.title}</p>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeedbackShowcase;
